import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, Scissors, Sun, CreditCard, Zap } from "lucide-react";
import { cn } from "../lib/utils";
import { getSettings } from "../services/settings";
import { useAuth } from "../auth/AuthContext";

interface QuickActionDropdownProps {
  onAction: (action: "Services" | "Sunbeds" | "Payment") => void;
  showPayment?: boolean;
}

const QuickActionDropdown: React.FC<QuickActionDropdownProps> = ({ onAction, showPayment = true }) => {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const isSunbedActive = React.useMemo(() => {
    const setting = user?.company?.is_sunbed;
    if (setting === undefined || setting === null) return false;
    if (typeof setting === 'string') return setting === 'active';
    return Number(setting) === 1 || Number(setting) > 0;
  }, [user]);

  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);


  const menuVariants: any = {
    hidden: {
      opacity: 0,
      scale: 0.95,
      y: -10,
      transition: { duration: 0.15, ease: [0.4, 0, 0.2, 1] }
    },
    visible: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: {
        duration: 0.2,
        ease: [0.4, 0, 0.2, 1],
        staggerChildren: 0.05
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -10 },
    visible: { opacity: 1, x: 0 }
  };

  return (
    <div
      className="relative inline-block text-left"
      ref={dropdownRef}
    >
      {/* Trigger Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          "flex items-center gap-2 text-white px-3 md:px-4 py-2 rounded-md hover:bg-white/10 transition h-10 border border-white/40 focus:outline-none ring-0 text-xs sm:text-sm shadow-sm",
          isOpen && "bg-white/10"
        )}
      >
        <Zap size={16} className="text-white fill-white" />
        <span className="font-bold hidden sm:inline">Quick</span>
      </button>

      {/* Dropdown Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial="hidden"
            animate="visible"
            exit="hidden"
            variants={menuVariants}
            className="absolute right-0 mt-2 w-48 sm:w-64 bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden z-[100] origin-top-right"
          >
            <div className="p-1.5 sm:p-2 space-y-0.5 sm:space-y-1">
              {/* Quick Services Option */}
              <motion.button
                variants={itemVariants}
                whileHover={{ backgroundColor: "rgba(136, 90, 187, 0.08)" }}
                onClick={() => {
                  onAction("Services");
                  setIsOpen(false);
                }}
                className="w-full flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2.5 sm:py-3.5 text-left text-gray-700 rounded-xl transition-colors duration-200 group"
              >
                <div className="bg-[#885ABB]/10 p-1.5 sm:p-2 rounded-lg group-hover:bg-[#885ABB] transition-colors duration-200">
                  <Scissors className="w-4 h-4 sm:w-5 sm:h-5 text-[#885ABB] group-hover:text-white transition-colors duration-200" />
                </div>
                <div>
                  <div className="font-bold text-xs sm:text-sm text-gray-900 leading-tight">Services</div>
                  <div className="text-[9px] sm:text-[10px] text-gray-400 font-medium leading-tight hidden xs:block">Add a service appointment</div>
                </div>
              </motion.button>

              {/* Quick Sunbeds Option */}
              {isSunbedActive && (
                <motion.button
                  variants={itemVariants}
                  whileHover={{ backgroundColor: "rgba(136, 90, 187, 0.08)" }}
                  onClick={() => {
                    onAction("Sunbeds");
                    setIsOpen(false);
                  }}
                  className="w-full flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2.5 sm:py-3.5 text-left text-gray-700 rounded-xl transition-colors duration-200 group"
                >
                  <div className="bg-[#885ABB]/10 p-1.5 sm:p-2 rounded-lg group-hover:bg-[#885ABB] transition-colors duration-200">
                    <Sun className="w-4 h-4 sm:w-5 sm:h-5 text-[#885ABB] group-hover:text-white transition-colors duration-200" />
                  </div>
                  <div>
                    <div className="font-bold text-xs sm:text-sm text-gray-900 leading-tight">Sunbeds</div>
                    <div className="text-[9px] sm:text-[10px] text-gray-400 font-medium leading-tight hidden xs:block">Add a sunbed session</div>
                  </div>
                </motion.button>
              )}

              {/* Quick Payment Option */}
              {showPayment && (
                <motion.button
                  variants={itemVariants}
                  whileHover={{ backgroundColor: "rgba(136, 90, 187, 0.08)" }}
                  onClick={() => {
                    onAction("Payment");
                    setIsOpen(false);
                  }}
                  className="w-full flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2.5 sm:py-3.5 text-left text-gray-700 rounded-xl transition-colors duration-200 group"
                >
                  <div className="bg-[#885ABB]/10 p-1.5 sm:p-2 rounded-lg group-hover:bg-[#885ABB] transition-colors duration-200">
                    <CreditCard className="w-4 h-4 sm:w-5 sm:h-5 text-[#885ABB] group-hover:text-white transition-colors duration-200" />
                  </div>
                  <div>
                    <div className="font-bold text-xs sm:text-sm text-gray-900 leading-tight">Payment</div>
                    <div className="text-[9px] sm:text-[10px] text-gray-400 font-medium leading-tight hidden xs:block">Process a fast checkout</div>
                  </div>
                </motion.button>
              )}
            </div>

            {/* Subtle Footer/Decoration */}
            <div className="h-1 bg-gradient-to-r from-transparent via-[#885ABB]/20 to-transparent" />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default QuickActionDropdown;
